import { useChat } from "@ai-sdk/react";
import { Button } from "@rulebase/core/components";
import {
  DefaultChatTransport,
  type ToolUIPart,
  type UIMessage,
} from "ai";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiArrowLeftLine, RiArrowUpLine } from "react-icons/ri";
import { api2BaseURL } from "../lib/env";
import { cn } from "../lib/utils";
import {
  Thread,
  ThreadContent,
  ThreadScrollToBottom,
} from "./ui/thread";
import { DotsLoader } from "./ui/loader";
import { Markdown } from "./ui/markdown";
import { Shimmer } from "./ui/shimmer";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
} from "./ui/message";
import {
  PromptInput,
  PromptInputActions,
  PromptInputTextarea,
} from "./ui/prompt-input";
import {
  buildTicketPageContext,
  type TicketContext,
} from "./ticket-context";
import { TicketContextChip } from "./ticket-context-chip";

export function KBChatView(props: {
  onBack: () => void;
  initialPrompt?: string;
  ticketContext?: TicketContext;
}) {
  const [chatId] = useState(() => crypto.randomUUID());
  const [input, setInput] = useState("");
  const sentInitialRef = useRef(false);
  const ticketContext = props.ticketContext;

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${api2BaseURL}/chats`,
        credentials: "include",
        prepareSendMessagesRequest({ messages, id, headers, body }) {
          const pageContext = ticketContext
            ? buildTicketPageContext(ticketContext)
            : undefined;

          return {
            body: {
              ...body,
              id,
              messages,
              ...(pageContext ? { pageContext } : {}),
            },
            headers,
          };
        },
      }),
    [ticketContext],
  );

  const { messages, sendMessage, setMessages, status, error } = useChat({
    id: chatId,
    transport,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadPersistedMessages() {
      const response = await fetch(`${api2BaseURL}/chats/${chatId}`, {
        credentials: "include",
      });
      if (!response.ok || cancelled) return;
      const payload = await response.json();
      if (!cancelled && payload.data?.messages?.length) {
        setMessages(payload.data.messages);
      }
    }

    loadPersistedMessages().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chatId, setMessages]);

  useEffect(() => {
    if (props.initialPrompt && !sentInitialRef.current) {
      sentInitialRef.current = true;
      sendMessage({ parts: [{ type: "text", text: props.initialPrompt }] });
    }
  }, [props.initialPrompt, sendMessage]);

  const handleSubmit = useCallback(
    (value: string) => {
      const text = value.trim();
      if (!text) return;

      sendMessage({ parts: [{ type: "text", text }] });
      setInput("");
    },
    [sendMessage],
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b px-2 py-3">
        <Button
          icon={RiArrowLeftLine}
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={props.onBack}
          type="button"
        />
        <div className="font-medium text-sm">Ask Knowledge Base</div>
      </div>

      <Thread className="space-y-0">
        {messages.length > 0 ? (
          <ThreadContent className="mx-auto w-full max-w-3xl space-y-4 px-2 py-6">
            {messages.map((message, index) => (
              <KBMessageComponent
                key={message.id}
                message={message}
                isLastMessage={index === messages.length - 1}
              />
            ))}
            {status === "submitted" && <LoadingMessage />}
            {status === "error" && error && <ErrorMessage error={error} />}
          </ThreadContent>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <p className="text-neutral-400">
              Ask a question about your knowledge base...
            </p>
          </div>
        )}
        <ThreadScrollToBottom />
      </Thread>

      <div className="max-w-3xl mx-auto w-full shrink-0 px-3 pb-3 flex flex-col gap-3">
        <PromptInput
          isLoading={status !== "ready"}
          value={input}
          onValueChange={setInput}
          onSubmit={() => handleSubmit(input)}
          className="border-neutral-200 text-neutral-700 bg-background relative z-10 w-full rounded-xl border p-0 pt-1"
        >
          <div className="flex flex-col">
            {ticketContext && <TicketContextChip context={ticketContext} />}
            <PromptInputTextarea
              placeholder="Ask a question..."
              className="text-foreground min-h-[44px] pt-2 pl-3 text-base leading-[1.3] sm:text-base md:text-base"
            />
            <PromptInputActions className="flex w-full items-center justify-end gap-2 p-2">
              <Button
                size="icon"
                disabled={
                  !input.trim() ||
                  (status !== "ready" && status !== "error")
                }
                onClick={() => handleSubmit(input)}
                className="size-6 rounded-md"
              >
                {status === "ready" || status === "error" ? (
                  <RiArrowUpLine className="size-3" />
                ) : (
                  <span className="size-3 rounded-xs bg-white" />
                )}
              </Button>
            </PromptInputActions>
          </div>
        </PromptInput>
      </div>
    </div>
  );
}

const KBMessageComponent = memo(
  ({ message, isLastMessage }: { message: UIMessage; isLastMessage: boolean }) => {
    const isAssistant = message.role === "assistant";

    const text = message.parts
      .map((part) => (part.type === "text" ? part.text : null))
      .join("");

    return (
      <Message
        className={cn(
          "mx-auto flex w-full flex-col gap-2",
          isAssistant ? "items-start" : "items-end",
        )}
      >
        {isAssistant ? (
          <div className="group flex w-full flex-col gap-1">
            <div className="flex w-full flex-col gap-3">
              {message.parts.map((part, index) => {
                if (part.type === "text") {
                  return (
                    <Markdown
                      key={index}
                      className="text-foreground prose w-full min-w-0 flex-1 rounded-lg bg-transparent p-0 text-base break-words whitespace-normal leading-relaxed"
                    >
                      {part.text}
                    </Markdown>
                  );
                }

                if (part.type.startsWith("tool-")) {
                  return (
                    <KBToolIndicator
                      key={`${part.type}-${index}`}
                      toolPart={part as ToolUIPart}
                    />
                  );
                }

                return null;
              })}
            </div>
            <MessageActions
              className={cn(
                "-ml-1.5 flex gap-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100",
                isLastMessage && "opacity-100",
              )}
            >
              <CopyAction text={text} />
            </MessageActions>
          </div>
        ) : (
          <div className="group flex w-full flex-col items-end gap-1">
            <MessageContent className="bg-muted text-foreground max-w-[85%] rounded-3xl px-5 py-2.5 whitespace-pre-wrap">
              {text}
            </MessageContent>
          </div>
        )}
      </Message>
    );
  },
);

KBMessageComponent.displayName = "KBMessageComponent";

function KBToolIndicator({ toolPart }: { toolPart: ToolUIPart }) {
  const { state, type } = toolPart;
  const isLoading = state !== "output-available";

  let label: string;
  if (type === "tool-ask_knowledge_base") {
    label = isLoading
      ? "Searching knowledge base..."
      : "Searched knowledge base";
  } else {
    label = isLoading ? "Working..." : "Done";
  }

  return (
    <div className="flex items-center gap-1.5 text-sm text-neutral-500">
      <span>{isLoading ? <Shimmer>{label}</Shimmer> : label}</span>
    </div>
  );
}

function CopyAction({ text }: { text: string }) {
  const [isCopied, setIsCopied] = useState(false);
  const [open, setOpen] = useState(false);

  async function onClick() {
    if (window.self !== window.top) {
      window.parent.postMessage(
        { type: "RULEBASE::COPY_TO_CLIPBOARD", text },
        "*",
      );
    } else {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // ignore
      }
    }
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  }

  return (
    <MessageAction
      tooltip={isCopied ? "Copied!" : "Copy"}
      delayDuration={100}
      open={open || isCopied}
      onOpenChange={setOpen}
    >
      <Button
        variant="ghost"
        size="icon"
        className="size-6 text-neutral-600"
        onClick={onClick}
      >
        {isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
    </MessageAction>
  );
}

const LoadingMessage = memo(() => (
  <Message className="mx-auto flex w-full flex-col items-start gap-2">
    <div className="group flex w-full flex-col gap-0">
      <div className="text-foreground prose w-full min-w-0 flex-1 rounded-lg bg-transparent p-0">
        <DotsLoader className="[&>div]:bg-neutral-400" />
      </div>
    </div>
  </Message>
));

LoadingMessage.displayName = "LoadingMessage";

const ErrorMessage = memo(({ error }: { error: Error }) => (
  <Message className="not-prose mx-auto flex w-full flex-col items-start gap-2">
    <div className="group flex w-full flex-col items-start gap-0">
      <div className="text-primary flex min-w-0 flex-1 flex-row items-center gap-2 rounded-lg border-2 border-red-300 bg-red-300/20 px-2 py-1">
        <AlertTriangle size={16} className="text-red-500" />
        <p className="text-red-500">{error.message}</p>
      </div>
    </div>
  </Message>
));

ErrorMessage.displayName = "ErrorMessage";
